import React, { useEffect, useState } from 'react'
import { Button, Col, Form, Modal, Row, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { createCard } from '../../../actions/cardActions'
import { cardCreateReset } from '../../../reducers/cardReducers'
import Loader from '../../Loader'
import Message from '../../Message'
import UpdateProfileForm from '../../UpdateProfileForm'
import { FaLock, FaInfoCircle, FaShieldAlt } from 'react-icons/fa'
import { SiLetsencrypt } from 'react-icons/si'
import './CreditCardStyles.css'
import { COLORS } from '../../../constants/colors'

function CreateCCModal({show, onHide, dispatch}) {
    
    const [cardNumber, setCardNumber] = useState('');
    const [cardOwner, setCardOwner] = useState('');
    const [expDate, setExpDate] = useState('');
    const [ccv, setCcv] = useState('');
    const [cardType, setCardType] = useState('credit');
    const [cardBrand, setCardBrand] = useState('unknown');
    const [isValid, setIsValid] = useState({
        cardNumber: false,
        cardOwner: false,
        expDate: false,
        ccv: false
    });

    const {
        cardReducers: { cardCreateSuccess, cardCreateError, cardCreateLoading }
    } = useSelector((state) => state);

    useEffect(() => {
        if(cardCreateSuccess){
            dispatch(cardCreateReset())
            onHide()
        }
    }, [cardCreateSuccess, dispatch, onHide]);

    // Function to detect card brand based on card number
    const detectCardBrand = (number) => {
        // Remove spaces and dashes
        const cleanNumber = number.replace(/[\s-]/g, '');
        
        // Basic regex patterns for card detection
        const patterns = {
            visa: /^4\d{12}(?:\d{3})?$/,
            mastercard: /^5[1-5]\d{14}$/
        };
        
        for (const [brand, pattern] of Object.entries(patterns)) {
            if (pattern.test(cleanNumber)) {
                return brand;
            }
        }
        
        return 'unknown';
    };

    // Function to validate card number using Luhn algorithm
    const validateCardNumber = (number) => {
        const cleanNumber = number.replace(/[\s-]/g, '');
        if (cleanNumber.length < 13 || cleanNumber.length > 19) return false;
        
        let sum = 0;
        let isEven = false;
        
        // Loop through values starting from the rightmost side
        for (let i = cleanNumber.length - 1; i >= 0; i--) {
            let digit = parseInt(cleanNumber.charAt(i), 10);
            
            if (isEven) {
                digit *= 2;
                if (digit > 9) {
                    digit -= 9;
                }
            }
            
            sum += digit;
            isEven = !isEven;
        }
        
        return (sum % 10) === 0;
    };

    // Function to validate expiration date
    const validateExpDate = (date) => {
        if (!date || date.length !== 5) return false;
        
        const [month, year] = date.split('/');
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear() % 100;
        const currentMonth = currentDate.getMonth() + 1;
        
        const expMonth = parseInt(month, 10);
        const expYear = parseInt(year, 10);
        
        if (expMonth < 1 || expMonth > 12) return false;
        
        if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
            return false;
        }
        
        return true;
    };

    // Update card brand when card number changes
    useEffect(() => {
        if (cardNumber.length >= 4) {
            const detectedBrand = detectCardBrand(cardNumber);
            setCardBrand(detectedBrand);
        } else {
            setCardBrand('unknown');
        }
    }, [cardNumber]);

    const submitHandler = async (e) => {
        e.preventDefault();

        // Validate all fields are present
        if (!cardOwner || !cardNumber || !expDate || !ccv || !cardType) {
            return;
        }

        // Format the card number (remove spaces)
        const clearCardNumber = cardNumber.replaceAll(' ', '');

        // Format the expiration date (ensure MM/YY format)
        const formattedExpDate = expDate.length === 5 ? expDate : expDate.padStart(5, '0');

        // Create the card data object with proper validation
        const cardData = {
            cardOwner: cardOwner.trim(),
            cardNumber: clearCardNumber,
            expDate: formattedExpDate,
            ccv: ccv.trim(),
            cardType: cardType,
            cardBrand: cardBrand
        };

        // Additional validation before submission
        if (!validateCardNumber(clearCardNumber)) {
            return;
        }

        if (!validateExpDate(formattedExpDate)) {
            return;
        }

        try {
            // Log the data being sent
            console.log('Submitting card data:', cardData);

            // Dispatch the create card action
            await dispatch(createCard(cardData));
            
            // Reset form fields
            setCardNumber('');
            setCardOwner('');
            setExpDate('');
            setCcv('');
            setCardType('credit');
            setCardBrand('unknown');
            
            // Close the modal
            onHide();
        } catch (error) {
            console.error('Error creating card:', error);
            // Error is already handled by the action
        }
    };

    // Update validation state when inputs change
    useEffect(() => {
        setIsValid({
            cardNumber: cardNumber.length >= 19 && validateCardNumber(cardNumber),
            cardOwner: cardOwner.length > 0,
            expDate: validateExpDate(expDate),
            ccv: ccv.length >= 3
        });
    }, [cardNumber, cardOwner, expDate, ccv]);

    return (
        <>
        <Modal show={show} onHide={onHide}>
            <Modal.Header className='border-0' closeButton />
            <Col className='fs-4 fw-bold text-center'>Add a Bank Card</Col>
            <Modal.Body>
                <UpdateProfileForm> 
                    {cardCreateError && <Message variant='danger'>{cardCreateError}</Message>}
                    {cardCreateLoading && <Loader />}
                    
                    <Form onSubmit={submitHandler}>
                    <Form.Group className="card-input-group">
                            <Form.Label className='my-2 fw-semibold'>
                                Card Type
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="card-type-tooltip" className="security-tooltip">
                                            Select whether this is a credit or debit card
                                        </Tooltip>
                                    }
                                >
                                    <FaInfoCircle className="card-info-tooltip" />
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Select
                                value={cardType}
                                onChange={(e) => setCardType(e.target.value)}
                            >
                                <option value="credit">Credit Card</option>
                                <option value="debit">Debit Card</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group controlId='CC Number' className="card-input-group">
                            <Form.Label className='my-2 fw-semibold'>
                                Card Number
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="card-number-tooltip" className="security-tooltip">
                                            We use industry-standard encryption to protect your card details
                                        </Tooltip>
                                    }
                                >
                                    <FaInfoCircle className="card-info-tooltip" />
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Control
                                type='text'
                                placeholder='Enter Card Number'
                                maxLength={19}
                                minLength={19}
                                value={cardNumber}
                                className={cardNumber.length > 0 ? (isValid.cardNumber ? 'card-field-valid' : 'card-field-invalid') : ''}
                                onChange={
                                    (e) => {
                                        let val = e.target.value.replaceAll(' ', '')
                                        if (/^[0-9\b]/.test(val) || val === '') {
                                            const matches = val.match(/.{1,4}/g)
                                            val = matches ? matches.join(' ') : ''
                                            setCardNumber(val)
                                        }
                                }}
                                inputMode='numeric'
                            />
                            {cardType && (
                                <img 
                                    src={cardType === 'visa' 
                                        ? 'https://raw.githubusercontent.com/slaterjohn/payment-logos/master/Rounded%20Corners/PNG/medium/visa@2x.png'
                                        : 'https://raw.githubusercontent.com/slaterjohn/payment-logos/master/Rounded%20Corners/PNG/medium/mastercard@2x.png'} 
                                    alt={cardType} 
                                    className="card-type-icon"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            )}
                        </Form.Group>

                        <Form.Group controlId='CC Owner' className="card-input-group">
                            <Form.Label className='my-2 fw-semibold'>
                                Card Owner
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="card-owner-tooltip" className="security-tooltip">
                                            Enter the name exactly as it appears on your card
                                        </Tooltip>
                                    }
                                >
                                    <FaInfoCircle className="card-info-tooltip" />
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Control
                                type='text'
                                placeholder='Enter Card Owner'
                                value={cardOwner}
                                className={cardOwner.length > 0 ? (isValid.cardOwner ? 'card-field-valid' : 'card-field-invalid') : ''}
                                onChange={(e) => setCardOwner(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="card-input-group">
                            <Form.Label className='my-2 fw-semibold'>
                                Expiration Date
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="exp-date-tooltip" className="security-tooltip">
                                            Enter the expiration date in MM/YY format
                                        </Tooltip>
                                    }
                                >
                                    <FaInfoCircle className="card-info-tooltip" />
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Control
                                type='text'
                                placeholder='MM/YY'
                                minLength={5}
                                maxLength={5}
                                value={expDate}
                                className={expDate.length > 0 ? (isValid.expDate ? 'card-field-valid' : 'card-field-invalid') : ''}
                                onChange={(e) => {
                                    let val = e.target.value.replace('/', '')
                                    if (/^[0-9]/.test(val) || val === '') {
                                        if(val.length === 2){
                                            const month = parseInt(val.substring(0,2), 10)
                                            if(month > 12){
                                                val = '12'
                                            }
                                        }
                                        const matches = val.match(/.{1,2}/g)
                                        val = matches ? matches.join('/') : ''
                                        setExpDate(val)
                                    }
                                }}
                            />
                        </Form.Group>

                        <Form.Group className="card-input-group">
                            <Form.Label className='my-2 fw-semibold'>
                                CVV
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="cvv-tooltip" className="security-tooltip">
                                            The 3-digit security code on the back of your card
                                        </Tooltip>
                                    }
                                >
                                    <FaInfoCircle className="card-info-tooltip" />
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Control
                                type='text'
                                placeholder='Enter CVV'
                                minLength={3}
                                maxLength={3}
                                value={ccv}
                                className={ccv.length > 0 ? (isValid.ccv ? 'card-field-valid' : 'card-field-invalid') : ''}
                                onChange={(e) => {
                                    let val = e.target.value
                                    if (/^[0-9]/.test(val) || val === '') {
                                        setCcv(val)
                                    }
                                }}
                            />
                        </Form.Group>

                        <Row className='d-flex flex-column justify-content-center'>
                            <Col className='d-flex justify-content-center'>
                                <Button 
                                    type='submit' 
                                    style={{ backgroundColor: COLORS.SAGE_GREEN, borderColor: COLORS.WARM_CONCRETE }}
                                    className='my-3 w-50'
                                    disabled={!isValid.cardNumber || !isValid.cardOwner || !isValid.expDate || !isValid.ccv}
                                >
                                    Save Bank Card
                                </Button>
                            </Col>               
                        </Row>
                    </Form>
                </UpdateProfileForm>
            </Modal.Body>
        </Modal>
        </>
    )
}

export default CreateCCModal
